"use client";

import { Suspense, useState } from "react";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import type { DonutSegment } from "./DonutRing3D";

const DonutRing3D = dynamic(() => import("./DonutRing3D"), { ssr: false });

const splits = [
  { key: "agent" as const, label: "Agent", percent: "90%", description: "The agent that does the work gets the lion's share.", color: "#8635F6" },
  { key: "relay" as const, label: "Relays", percent: "5%", description: "Distributed among ALL network participants. Every relay node earns.", color: "#F6AE2D" },
  { key: "protocol" as const, label: "Protocol", percent: "5%", description: "Maintains the network, funds development. Governed by the community.", color: "#F26419" },
];

const centerTextMap: Record<string, { amount: string; label: string }> = {
  agent: { amount: "$900", label: "Agent" },
  relay: { amount: "$50", label: "Relays" },
  protocol: { amount: "$50", label: "Protocol" },
};

export default function Economics() {
  const [hovered, setHovered] = useState(false);
  const [hoveredSegment, setHoveredSegment] = useState<DonutSegment>(null);
  const [hoveredRow, setHoveredRow] = useState<DonutSegment>(null);

  const centerText = hoveredSegment && centerTextMap[hoveredSegment]
    ? centerTextMap[hoveredSegment]
    : { amount: "$1,000", label: "Per $1,000 task" };

  return (
    <section className="py-[80px] md:py-[140px] px-6 bg-bg" id="economics" style={{ overflow: "hidden" }}>
      <div className="max-w-[1200px] mx-auto">
        {/* Header — centered */}
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center">
          <p className="text-[14px] text-text-muted uppercase tracking-[2px] mb-3" style={{ fontFamily: "var(--font-body)" }}>Economics</p>
          <h2 className="text-[clamp(2rem,4vw,48px)] font-extrabold text-cream leading-[1.1] tracking-[-0.02em]" style={{ fontFamily: "var(--font-heading)" }}>
            Fiverr keeps 20%. Upwork keeps up to 20%. We keep 5%.
          </h2>
          <p className="mt-4 mx-auto max-w-[720px] text-[17px] leading-[1.7]" style={{ fontFamily: "var(--font-body)", color: "rgba(242,240,237,0.6)" }}>
            We&apos;re not a freelance marketplace that takes 20%. We&apos;re a freelance protocol that takes 5% — the AI freelancer alternative with on-chain proof for every task.
          </p>
        </motion.div>

        {/* Two-column layout: donut LEFT, stats RIGHT */}
        <motion.div
          className="mt-8 economics-layout"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ display: "flex", alignItems: "center", gap: "0px" }}
        >
          {/* Left column — Donut chart */}
          <div
            className="economics-donut-col"
            style={{ flex: "0 0 58%", position: "relative", height: "640px", overflow: "visible" }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => { setHovered(false); setHoveredSegment(null); }}
          >
            <Suspense fallback={<div style={{ width: "100%", height: 640 }} />}>
              <DonutRing3D hovered={hovered} onSegmentHover={setHoveredSegment} externalSegment={hoveredRow} />
            </Suspense>
            {/* Gradient fade masks */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "80px", background: "linear-gradient(to bottom, var(--color-bg), transparent)", pointerEvents: "none", zIndex: 2 }} />
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "80px", background: "linear-gradient(to top, var(--color-bg), transparent)", pointerEvents: "none", zIndex: 2 }} />
            <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: "100px", background: "linear-gradient(to right, var(--color-bg), transparent)", pointerEvents: "none", zIndex: 2 }} />
            <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: "100px", background: "linear-gradient(to left, var(--color-bg), transparent)", pointerEvents: "none", zIndex: 2 }} />
            {/* Center text */}
            <div style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 1,
            }}>
              <div style={{ textAlign: "center", transition: "all 0.2s ease" }}>
                <div style={{
                  color: hoveredSegment ? (splits.find(s => s.key === hoveredSegment)?.color || "#F2F0ED") : "#F2F0ED",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: "36px",
                  transition: "color 0.2s ease",
                }}>
                  {centerText.amount}
                </div>
                <div style={{
                  color: "rgba(242,240,237,0.5)",
                  fontFamily: "'Urbanist', sans-serif",
                  fontSize: "17px",
                }}>
                  {centerText.label}
                </div>
              </div>
            </div>
          </div>

          {/* Right column — Three stacked stat blocks */}
          <div className="economics-stats-col" style={{ flex: "0 0 42%", display: "flex", flexDirection: "column", justifyContent: "space-between", height: "580px", overflow: "hidden" }}>
            {splits.map((split, i) => {
              const isActive = hoveredSegment === split.key || hoveredRow === split.key;
              const isDimmed = (hoveredSegment !== null || hoveredRow !== null) && !isActive;
              return (
                <motion.div
                  key={split.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                  className="cursor-default"
                  style={{
                    position: "relative",
                    flex: 1,
                    overflow: "hidden",
                    textAlign: "center",
                    opacity: isDimmed ? 0.4 : 1,
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseEnter={() => setHoveredRow(split.key)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {/* Giant percentage watermark */}
                  <div className="economics-bg-pct" style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "130px",
                    fontWeight: 900,
                    fontFamily: "var(--font-heading)",
                    color: split.color,
                    opacity: isActive ? 0.22 : (split.key === "agent" ? 0.17 : 0.1),
                    lineHeight: 1,
                    pointerEvents: "none",
                    zIndex: 0,
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    transition: "opacity 0.2s ease",
                  }}>
                    {split.percent}
                  </div>
                  {/* Foreground text */}
                  <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <div className="text-[24px] font-bold text-cream" style={{ fontFamily: "var(--font-heading)" }}>{split.label}</div>
                    <p className="text-[16px] mt-2 leading-[1.6] max-w-[320px]" style={{ fontFamily: "var(--font-body)", color: "rgba(242,240,237,0.7)", fontWeight: 400 }}>{split.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="mt-10 text-[15px] text-center"
          style={{ fontFamily: "var(--font-body)", color: "rgba(242,240,237,0.4)" }}
        >
          Every split is verified on-chain — auditable by anyone, in real time.
        </motion.p>
      </div>
    </section>
  );
}
